import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { CnBalloon, CnIcon, CnTooltip, CnButton } from '@sto/cn-ui';
import styles from './index.module.scss'; // 用于样式
import RobotList from './components/robot-list';
import Global from '@/utils/global';
import { useChatStore } from '@/store';
import { correctIframeCoordinates, addIframeEventListeners, removeIframeEventListeners } from '@/utils/iframeUtils';

let offset = { x: 0, y: 0 };
let resizeOffset = { 
  x: 0, 
  y: 0, 
  initialSize: { width: 0, height: 0 }, 
  initialPosition: { left: 0, top: 0 } 
};
const minWidth = 710;
const minHeight = 700;
const DraggableModal = (props) => {
  const { onClose, visible, children, setVoiceSwitch, voiceSwitch, onChangeRobot, robotList, robotInfo: defaultRobot } = props;
  const robotListRef = useRef(null);
  const modalBody = useRef(null);
  const [changeRobotVisible, setChangeRobotVisible] = useState(false);
  const [robotInfo, setRobotInfo] = useState(defaultRobot);
  const [isInitialize, setInitialize] = useState(false);
  const [windowState, setWindowState] = useState('float'); // float: 窗口浮动; full: 全屏; fixed: 右固定
  const { setWindowState: setStoreWindowState, setFixedStyle: setStoreFixedStyle, setActiveSessionId, 
    setHistorySessionId, setActiveHistoryId, setIsNewChat, setActiveContentType, setCurrentRobotCode } = useChatStore();
  const [position, setPosition] = useState({
    top: window.innerHeight * 0.1,
    left: window.innerWidth / 2 - 477,
  });
  const modalRef = useRef(null);
  // 拖拽的方向，用于判断是哪个方向的缩放
  const resizeDirectionRef = useRef(null);
  const [size, setSize] = useState({
    width: 954, // 浮动窗口初始的窗口宽度: 800px + 154px(历史对话记录)
    height: window.innerHeight * 0.8,
  });

  const handleMouseDown = (e) => {
    offset = { x: e.pageX, y: e.pageY };
    (document.querySelector("#modalBody") as HTMLElement).style.pointerEvents = 'none';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = (e) => {
    const mLeft = e.pageX - offset.x;
    const mTop = e.pageY - offset.y;
    const newLeft = position.left + mLeft;
    const newTop = position.top + mTop;

    // 限制弹窗不超出屏幕
    const { innerWidth, innerHeight } = window;
    setPosition({
      left: Math.max(-320, Math.min(newLeft, innerWidth - 80)),
      top: Math.max(0, Math.min(newTop, innerHeight - 40)),
    });
  };

  const handleMouseUp = () => {
    (document.querySelector("#modalBody") as HTMLElement).style.pointerEvents = 'auto';
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  // 缩放调整处理
  const handleResizeMouseDown = (e, direction) => {
    e.preventDefault();
    e.stopPropagation();
    
    resizeDirectionRef.current = direction;

    const { pageX, pageY } = correctIframeCoordinates(e);
    
    // 记录缩放开始时的初始状态
    resizeOffset = { 
      x: pageX, 
      y: pageY,
      initialSize: { ...size },
      initialPosition: { ...position }
    };
    
    document.addEventListener('mousemove', handleResizeMouseMove);
    document.addEventListener('mouseup', handleResizeMouseUp);

    // 为所有iframe添加事件监听器
    addIframeEventListeners('mousemove', handleResizeMouseMove);
    addIframeEventListeners('mouseup', handleResizeMouseUp);
  };


  const handleResizeMouseMove = (e) => {
    if (!resizeDirectionRef.current) return;
    
    const { pageX, pageY } = correctIframeCoordinates(e);
    const deltaX = pageX - resizeOffset.x;
    const deltaY = pageY - resizeOffset.y;
    
    // 使用初始状态作为基准
    let newWidth = resizeOffset.initialSize.width;
    let newHeight = resizeOffset.initialSize.height;
    let newLeft = resizeOffset.initialPosition.left;
    let newTop = resizeOffset.initialPosition.top;
    
    
    // 根据拖拽方向调整大小和位置
    if (resizeDirectionRef.current.includes('right')) {
      newWidth = Math.max(minWidth, resizeOffset.initialSize.width + deltaX);
    }
    if (resizeDirectionRef.current.includes('left')) {
      const maxDelta = resizeOffset.initialSize.width - minWidth;
      const actualDelta = Math.min(deltaX, maxDelta);
      newWidth = Math.max(minWidth, resizeOffset.initialSize.width - actualDelta);
      newLeft = resizeOffset.initialPosition.left + actualDelta;
    }
    if (resizeDirectionRef.current.includes('bottom')) {
      newHeight = Math.max(minHeight, resizeOffset.initialSize.height + deltaY);
    }
    if (resizeDirectionRef.current.includes('top')) {
      const maxDelta = resizeOffset.initialSize.height - minHeight;
      const actualDelta = Math.min(deltaY, maxDelta);
      newHeight = Math.max(minHeight, resizeOffset.initialSize.height - actualDelta);
      newTop = resizeOffset.initialPosition.top + actualDelta;
    }
    
    // 限制最大尺寸
    const { innerWidth, innerHeight } = window;
    newWidth = Math.min(newWidth, innerWidth - 40);
    newHeight = Math.min(newHeight, innerHeight - 40);
    
    // 限制最小位置
    newLeft = Math.max(0, Math.min(newLeft, innerWidth - newWidth));
    newTop = Math.max(0, Math.min(newTop, innerHeight - newHeight));
    
    
    // 直接更新状态
    setSize({ width: newWidth, height: newHeight });
    setPosition({ left: newLeft, top: newTop });
  };

  const handleResizeMouseUp = () => {
    resizeDirectionRef.current = null;
    document.removeEventListener('mousemove', handleResizeMouseMove);
    document.removeEventListener('mouseup', handleResizeMouseUp);

    // 从所有iframe移除事件监听器
    removeIframeEventListeners('mousemove', handleResizeMouseMove);
    removeIframeEventListeners('mouseup', handleResizeMouseUp);
  };

  const fixedStyle = useMemo(() => {
    if (windowState === 'full') {
      return {
        left: position.left,
        top: position.top,
        height: window.innerHeight,
        width: window.innerWidth,
      };
    } else if (windowState === 'fixed') {
      return { left: window.innerWidth - 940, right: 0, top: 0, height: window.innerHeight, width: 940 }; // 窗口吸附: 460px + 154px(历史对话记录)
    }
    return {
      left: position.left,
      top: position.top,
      height: size.height,
      width: size.width,
    };
  }, [position.left, position.top, windowState, size.width, size.height]);

  useEffect(() => {
    setStoreFixedStyle(fixedStyle);
  }, [fixedStyle]);

  const tools = useMemo(() => {
    const toolList = [
      {
        type: 'float',
        icon: 'https://imgcdn.sto.cn/5E030826-C63D-40-16x16-%E7%AA%97%E5%8F%A3%E7%8B%AC%E7%AB%8B.svg',
        alt: '窗口浮动',
        onClick: () => {
          setWindowState('float');
          setStoreWindowState('float');
          setPosition({
            top: window.innerHeight * 0.1,
            left: window.innerWidth / 2 - 477,
          });
        },
      },
      {
        type: 'fixed',
        icon: 'https://imgcdn.sto.cn/F42039F1-FBF2-4C-16x16-%E5%90%B8%E9%99%84.svg',
        alt: '窗口吸附',
        onClick: () => {
          setWindowState('fixed');
          setStoreWindowState('fixed');
        },
      },
      {
        type: 'full',
        icon: 'https://imgcdn.sto.cn/15ADAFB2-5162-4C-16x16-%E6%94%BE%E5%A4%A7.svg',
        alt: '窗口全屏',
        onClick: () => {
          setWindowState('full');
          setStoreWindowState('full');
          setPosition({
            top: 0,
            left: 0,
          });
        },
      },
      {
        type: 'close',
        icon: 'https://imgcdn.sto.cn/CD337EF9-7832-42-16x16-%E5%85%B3%E9%97%AD.svg',
        alt: '',
        onClick: () => onClose(),
      },
    ];

    return toolList?.filter(({ type }) => type !== windowState);
  }, [onClose, windowState]);

  /** 开启新会话 */
  const onNewChatClick = useCallback(() => {
    setActiveSessionId('');
    setHistorySessionId('');
    setActiveHistoryId('');
    setIsNewChat(true);
    setActiveContentType('chat');
  }, []);

  /**
   * 切换机器人
   */
  const onChooseRobot = useCallback(() => {
    onNewChatClick(); // 机器人切换后，开启新会话
    Global.robotInfo = robotListRef.current.choosed;
    setRobotInfo(Global.robotInfo)
    localStorage.setItem('defaultRobotCode', robotListRef.current.choosed.robotCode)
    setCurrentRobotCode(robotListRef.current.choosed.robotCode);
    onChangeRobot()
    setChangeRobotVisible(false)
    setTimeout(() => {
      onChangeRobot(Global.robotInfo);
    }, 200)
  }, [robotListRef]);

  useEffect(() => {
    setInitialize((v) => v || visible);
  }, [visible]);

  return (
    isInitialize ? (
      <div
        className={styles.modal}
        style={{ ...fixedStyle, display: visible ? 'block' : 'none' }}
        ref={modalRef}
      >
        {/* 头部区域 */}
        <div className={styles.modalHeader} onMouseDown={handleMouseDown}>
          <div className={styles.robotInfo}>
            <img
              className={styles.modalTitle}
              src="https://imgcdn.sto.cn/99279BF6-2EB8-47-158x36-logo-header.png"
              alt=""
            />
            {/* 非测试机器人 */}
            {!Global.fetchHeader.isTestChat && <div className={styles.robotName}>{robotInfo.robotName ?? ''}</div>}
            {/* 测试机器人 */}
            {robotList?.length ? (
              <CnBalloon
                triggerType="click"
                visible={changeRobotVisible}
                title="仅可选择一个机器人"
                style={{ maxWidth: 'unset'}}
                onClose={() => setChangeRobotVisible(false)}
                trigger={
                  <div className={styles.robot} onClick={() => robotList?.length && setChangeRobotVisible(true)}>
                    <span>{robotInfo.robotName ?? ''}</span>
                    {robotList?.length && <CnIcon type="exchange" className={styles.changeIcon} size="medium" />}
                  </div>
                }
                align="b"
              >
                <RobotList robotInfo={robotInfo} ref={robotListRef} list={robotList} />
                <div className={styles.footer}>
                  <CnButton size="small" onClick={() => setChangeRobotVisible(true)}>取消</CnButton>
                  <CnButton style={{ marginLeft: 15 }} size="small" type="primary" onClick={onChooseRobot}>确认</CnButton>
                </div>
              </CnBalloon>
            ) : null}
          </div>
          <div className={styles.modalToolbar}>
            {setVoiceSwitch ? (
              <CnTooltip
                style={{ zIndex: 9998 }}
                trigger={
                  <img
                    onClick={() => setVoiceSwitch()}
                    src={
                      voiceSwitch
                        ? 'https://imgcdn.sto.cn/B8A813E9-4F07-41-16x16-%E5%A3%B0%E9%9F%B3.svg'
                        : 'https://imgcdn.sto.cn/E232235F-FEB3-4E-16x16-%E9%9D%99%E9%9F%B3.svg'
                    }
                    alt=""
                  />
                }
              >
                {voiceSwitch ? '关闭语音播报' : '开启语音播报'}
              </CnTooltip>
            ) : null}
            {tools?.map(({ icon, onClick, alt }, index) =>
              alt ? (
                <React.Fragment key={`tooltip-${index}`}>
                  <CnTooltip
                    style={{ zIndex: 1001 }}
                    trigger={
                      <img src={icon} alt={alt} onClick={onClick} />
                    }
                  >
                    {alt}
                  </CnTooltip>
                </React.Fragment>
              ) : (
                <img key={`img-${index}`} src={icon} alt="" onClick={onClick} />
              ),
            )}
          </div>
        </div>
        {/* 主体内容区域 */}
        <div className={styles.modalBody} id="modalBody" ref={modalBody}>
          {children}
        </div>

        {/* 缩放手柄 - 只在浮动状态下显示 */}
        {windowState === 'float' && (
          <>
            {/* 左边缩放手柄 */}
            <div 
              className={`${styles.resizeHandle} ${styles.resizeLeft}`}
              onMouseDown={(e) => handleResizeMouseDown(e, 'left')}
              title="拖拽调整左边"
            />
            
            {/* 右边缩放手柄 */}
            <div 
              className={`${styles.resizeHandle} ${styles.resizeRight}`}
              onMouseDown={(e) => handleResizeMouseDown(e, 'right')}
              title="拖拽调整右边"
            />
            
            {/* 上边缩放手柄 */}
            <div 
              className={`${styles.resizeHandle} ${styles.resizeTop}`}
              onMouseDown={(e) => handleResizeMouseDown(e, 'top')}
              title="拖拽调整上边"
            />
            
            {/* 下边缩放手柄 */}
            <div 
              className={`${styles.resizeHandle} ${styles.resizeBottom}`}
              onMouseDown={(e) => handleResizeMouseDown(e, 'bottom')}
              title="拖拽调整下边"
            />
            
            {/* 左上角缩放手柄 */}
            <div 
              className={`${styles.resizeHandle} ${styles.resizeTopLeft}`}
              onMouseDown={(e) => handleResizeMouseDown(e, 'top left')}
              title="拖拽调整左上角"
            />
            
            {/* 右上角缩放手柄 */}
            <div 
              className={`${styles.resizeHandle} ${styles.resizeTopRight}`}
              onMouseDown={(e) => handleResizeMouseDown(e, 'top right')}
              title="拖拽调整右上角"
            />
            
            {/* 左下角缩放手柄 */}
            <div 
              className={`${styles.resizeHandle} ${styles.resizeBottomLeft}`}
              onMouseDown={(e) => handleResizeMouseDown(e, 'bottom left')}
              title="拖拽调整左下角"
            />
            
            {/* 右下角缩放手柄 */}
            <div 
              className={`${styles.resizeHandle} ${styles.resizeBottomRight}`}
              onMouseDown={(e) => handleResizeMouseDown(e, 'bottom right')}
              title="拖拽调整右下角"
            />
          </>
        )}
      </div>
    ) : null
  );
};

export default DraggableModal;
